import { createPortal } from "react-dom";
import { Rect } from "PosterEditor/model/poster-types";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import {
  MIN_CONTENT_SCALE,
  MAX_CONTENT_SCALE,
} from "PosterEditor/utils/canvas-drawing/canvas-drawing-gallery";
import styles from "./ViewportToolbar.module.css";

interface ViewportToolbarProps {
  viewportId: string;
  viewportRect: Rect;
  mapContainerRect: DOMRect;
  isAdmin: boolean;
  hasImage: boolean;
  currentScale: number;
  onSplitHorizontal: () => void;
  onSplitVertical: () => void;
  onReplaceImage: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onDelete: () => void;
}

interface ToolbarButton {
  id: string;
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
}

interface ToolbarSection {
  id: string;
  buttons: ToolbarButton[];
}

const BUTTON_SIZE = 20;
const TOOLBAR_OFFSET = 8;

const SplitHorizontalIcon = () => (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
    <line x1="6" y1="1" x2="6" y2="11" stroke="white" strokeWidth="1.5" />
    <line x1="3" y1="4" x2="3" y2="8" stroke="white" strokeWidth="1.5" />
    <line x1="9" y1="4" x2="9" y2="8" stroke="white" strokeWidth="1.5" />
  </svg>
);

const SplitVerticalIcon = () => (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
    <line x1="1" y1="6" x2="11" y2="6" stroke="white" strokeWidth="1.5" />
    <line x1="4" y1="3" x2="8" y2="3" stroke="white" strokeWidth="1.5" />
    <line x1="4" y1="9" x2="8" y2="9" stroke="white" strokeWidth="1.5" />
  </svg>
);

const MinusIcon = () => (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
    <line x1="2" y1="6" x2="10" y2="6" stroke="white" strokeWidth="2" />
  </svg>
);

export const ViewportToolbar = ({
  viewportRect,
  mapContainerRect,
  isAdmin,
  hasImage,
  currentScale,
  onSplitHorizontal,
  onSplitVertical,
  onReplaceImage,
  onZoomIn,
  onZoomOut,
  onDelete,
}: ViewportToolbarProps) => {
  const canZoomIn = hasImage && currentScale < MAX_CONTENT_SCALE;
  const canZoomOut = hasImage && currentScale > MIN_CONTENT_SCALE;

  const buildSections = (): ToolbarSection[] => {
    const sections: ToolbarSection[] = [];

    if (isAdmin) {
      sections.push({
        id: "split",
        buttons: [
          {
            id: "split-horizontal",
            icon: <SplitHorizontalIcon />,
            tooltip: "Split right",
            onClick: onSplitHorizontal,
          },
          {
            id: "split-vertical",
            icon: <SplitVerticalIcon />,
            tooltip: "Split down",
            onClick: onSplitVertical,
          },
        ],
      });
    }

    sections.push({
      id: "replace",
      buttons: [
        {
          id: "replace-image",
          icon: <UIIcon iconName="PHOTO" variant="CONTRAST" size={14} />,
          tooltip: hasImage ? "Replace image" : "Add image",
          onClick: onReplaceImage,
        },
      ],
    });

    if (hasImage) {
      sections.push({
        id: "zoom",
        buttons: [
          {
            id: "zoom-out",
            icon: <MinusIcon />,
            tooltip: "Zoom out",
            onClick: onZoomOut,
            disabled: !canZoomOut,
          },
          {
            id: "zoom-in",
            icon: <UIIcon iconName="PLUS" variant="CONTRAST" size={14} />,
            tooltip: "Zoom in",
            onClick: onZoomIn,
            disabled: !canZoomIn,
          },
        ],
      });
    }

    if (isAdmin) {
      sections.push({
        id: "delete",
        buttons: [
          {
            id: "delete-viewport",
            icon: <UIIcon iconName="BIN" variant="CONTRAST" size={14} />,
            tooltip: "Delete viewport",
            onClick: onDelete,
          },
        ],
      });
    }

    return sections;
  };

  const sections = buildSections();

  // Calculate toolbar width to center it
  const buttonCount = sections.reduce((sum, s) => sum + s.buttons.length, 0);
  const sectionGaps = (sections.length - 1) * 12;
  const buttonGaps = sections.reduce(
    (sum, s) => sum + (s.buttons.length - 1) * 4,
    0
  );
  const toolbarWidth =
    buttonCount * BUTTON_SIZE + sectionGaps + buttonGaps + 16; // 16 for padding

  // Position toolbar centered below viewport
  const viewportCenterX = viewportRect.x + viewportRect.width / 2;
  let toolbarLeft = mapContainerRect.x + viewportCenterX - toolbarWidth / 2;
  let toolbarTop =
    mapContainerRect.y + viewportRect.y + viewportRect.height + TOOLBAR_OFFSET;

  // Clamp to stay within map container horizontally
  const minLeft = mapContainerRect.x + 4;
  const maxLeft = mapContainerRect.x + mapContainerRect.width - toolbarWidth - 4;
  toolbarLeft = Math.max(minLeft, Math.min(maxLeft, toolbarLeft));

  // If toolbar would overflow bottom, position above viewport
  const toolbarHeight = BUTTON_SIZE + 16; // button + padding
  if (toolbarTop + toolbarHeight > mapContainerRect.y + mapContainerRect.height) {
    toolbarTop = mapContainerRect.y + viewportRect.y - toolbarHeight - TOOLBAR_OFFSET;
  }

  // Prevent clicks on toolbar from propagating to map (which would deselect the viewport)
  const handleToolbarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleButtonClick = (e: React.MouseEvent, onClick: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    onClick();
  };

  return createPortal(
    <div
      className={styles.toolbar}
      style={{
        position: "absolute",
        left: `${toolbarLeft}px`,
        top: `${toolbarTop}px`,
      }}
      onClick={handleToolbarClick}
      onMouseDown={handleToolbarClick}
    >
      {sections.map((section, sectionIndex) => (
        <div key={section.id} className={styles.section}>
          {section.buttons.map((button) => (
            <button
              key={button.id}
              className={styles.button}
              onClick={(e) => handleButtonClick(e, button.onClick)}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={button.disabled}
              title={button.tooltip}
              style={{
                width: BUTTON_SIZE,
                height: BUTTON_SIZE,
              }}
            >
              {button.icon}
            </button>
          ))}
          {sectionIndex < sections.length - 1 && (
            <div className={styles.sectionDivider} />
          )}
        </div>
      ))}
    </div>,
    document.body
  );
};
